/**
 * BuildPlanCard — the typed card for `propose_build_plan`
 * (ChatMessage.TOOL_CARDS; backend allowlist agent_types.RENDERED_TOOLS).
 *
 * Renders Arc's validated build plan — scope sentence, concrete counts, date
 * passes, cost band — with a Run button. Run goes through the lane's context
 * handler → the deterministic /build/run endpoint (which re-validates the
 * plan server-side) — Arc never writes entries. Outside a build lane the
 * card renders read-only.
 */
import React, { useMemo, useState } from 'react';

import { useChronologyBuild } from './chronologyBuildContext';

const S = {
  card: { marginTop: 8, border: '1px solid #d9cdf5', borderRadius: 8, background: '#faf8ff', overflow: 'hidden', fontSize: 13 },
  head: { padding: '8px 12px', background: '#efe8fd', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 },
  body: { padding: '8px 12px' },
  scope: { fontSize: 13, fontWeight: 600 },
  rationale: { fontSize: 12, color: '#667', padding: '4px 0' },
  stats: { display: 'flex', gap: 14, flexWrap: 'wrap', padding: '6px 0' },
  stat: { fontSize: 12.5, color: '#334' },
  statNum: { fontWeight: 700, fontSize: 15, display: 'block' },
  passes: { fontSize: 12, color: '#556', padding: '4px 0' },
  note: { fontSize: 11.5, color: '#8a7fae', padding: '4px 0' },
  rejected: { color: '#b0651a', fontSize: 13, padding: '6px 0' },
  actions: { display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid #eee6fb', background: '#fdfcff', flexWrap: 'wrap' },
  btnPrimary: { padding: '5px 14px', borderRadius: 6, border: '1px solid #5b3fa8', background: '#5b3fa8', color: '#fff', cursor: 'pointer', fontSize: 13 },
  hint: { fontSize: 12, color: '#8a7fae' },
  report: { fontSize: 13, color: '#2c6e49', fontWeight: 600 },
  reportMeta: { fontSize: 12, color: '#556', fontWeight: 400 },
  err: { color: '#c0392b', fontSize: 12 },
};

function Stat({ label, value }) {
  return (
    <span style={S.stat}>
      <span style={S.statNum}>{value}</span>
      {label}
    </span>
  );
}

export default function BuildPlanCard({ result }) {
  const build = useChronologyBuild();
  const plan = result?.plan;
  const est = result?.estimates || {};
  const planKey = useMemo(() => JSON.stringify(plan || {}), [plan]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  if (result?.status === 'rejected') {
    return (
      <div style={S.card}>
        <div style={S.head}>✨ Build plan — rejected</div>
        <div style={S.body}>
          <div style={S.rejected}>{result.error}</div>
          <div style={S.hint}>Arc can narrow the scope and propose again.</div>
        </div>
      </div>
    );
  }
  if (!plan) return null;

  const canRun = !!build && build.chronologyId === result.chronology_id;
  const report = build?.reports?.[planKey];
  const arc = plan.date_passes?.arc || {};
  const [lo, hi] = est.arc_cost_band_usd || [0, 0];

  const runPlan = async () => {
    if (!canRun || running) return;
    setRunning(true); setError('');
    try {
      await build.run(plan, planKey);
    } catch (e) {
      setError(e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={S.card}>
      <div style={S.head}>✨ Build plan</div>
      <div style={S.body}>
        {plan.scope_description && <div style={S.scope}>{plan.scope_description}</div>}
        {plan.rationale && <div style={S.rationale}>{plan.rationale}</div>}
        <div style={S.stats}>
          <Stat label="entries to draft" value={est.would_draft ?? '—'} />
          <Stat label="of them undated" value={est.of_which_undated ?? '—'} />
          {est.current_undated_entries > 0 && (
            <Stat label="already undated here" value={est.current_undated_entries} />
          )}
          {arc.enabled && (
            <Stat
              label={`Arc date call${est.arc_date_calls === 1 ? '' : 's'} (~$${lo.toFixed(2)}–$${hi.toFixed(2)})`}
              value={est.arc_date_calls ?? 0}
            />
          )}
        </div>
        <div style={S.passes}>
          Date passes: {plan.date_passes?.deterministic ? 'free parser scan' : 'none'}
          {arc.enabled && <> + Arc pass (max {arc.max_entries} entries, quote-verified candidates)</>}
        </div>
        <div style={S.note}>
          Everything lands as <strong>suggested</strong> entries for your review —
          nothing exports until you accept it. Dates arrive as pick-able candidates,
          never confirmed dates.
        </div>
      </div>
      <div style={S.actions}>
        {report ? (
          <span style={S.report}>
            ✓ Ran — {report.drafted} drafted
            {report.dated_deterministic > 0 && <>, dates suggested for {report.dated_deterministic}</>}
            {report.undated_remaining > 0 && <>, {report.undated_remaining} still undated</>}
            {report.arc_job_id && (
              <span style={S.reportMeta}> · Arc date pass running in the background — watch the jobs banner</span>
            )}
          </span>
        ) : canRun ? (
          <>
            <button style={S.btnPrimary} onClick={runPlan} disabled={running || !!build.busy}>
              {running ? 'Running…' : `Run this plan (${est.would_draft ?? 0} entries)`}
            </button>
            <span style={S.hint}>Or tell Arc what to change — it will propose a new plan.</span>
          </>
        ) : (
          <span style={S.hint}>Open Build with Arc on the Chronology tab to run this plan.</span>
        )}
        {error && <span style={S.err}>{error}</span>}
      </div>
    </div>
  );
}
